/**
 * @fileoverview アルコール測定値の単位変換と低濃度域の扱い
 * @description 検知器(AN-S126a)は 4バイトパケットの3バイト目に測定値を
 *   「1 = 0.001 mg/L」の生バイト(0〜255)で返す。コンパニオンアプリ・WebUSB・
 *   PCバックエンドSSE のどの経路でも、船員アプリへ届くのは生バイトのまま。
 *   mg/L への変換は、必ずこのモジュールを通して行う。
 *
 *   低濃度域の扱い:
 *   本機は半導体式センサーのため、化学式に比べて低濃度域の精度が落ちる。
 *   メーカー(慶洋エンジニアリング)によると、誤検知を避けるため純正アプリでは
 *   0.01〜0.05 mg/L を 0.00 mg/L として扱っている(2026-09-16 メーカー回答)。
 *   検知器本体の表示とCATMSの記録が食い違うと点呼記録として混乱を招くため、
 *   当システムでも同じ扱いに揃える。全社共通の挙動とし、会社別の切り替えは設けない。
 *
 *   手入力の値はこの正規化の対象外。別の機種の数値を読み取って入力する場合が
 *   あるため、入力されたとおりに記録する。
 */

/**
 * 検知器の「測定値なし」番兵バイト。0xEE(238)=呼気未検知、0xAA(170)=パケット開始と同値。
 * 実測値ではないので、結果（mg/L）に変換してはいけない（238をそのまま変換すると
 * 0.238 mg/L＝法定基準値超過の誤判定になる。2026-10-21 栄希で発覚）。
 * 受け取った側は「息が感知されなかった」として再測定を促すこと。
 */
export const ALCOHOL_SENTINEL_RAWS: ReadonlySet<number> = new Set([0xAA, 0xEE]);

/** 生バイトが番兵値（測定値なし）かどうか */
export function isAlcoholSentinelRaw(raw: number): boolean {
  return ALCOHOL_SENTINEL_RAWS.has(raw);
}

/** これ以下の生バイトは測定値なし(0.000 mg/L)として扱う。50 = 0.050 mg/L */
export const ALCOHOL_DEADBAND_MAX_RAW = 50;

/** 低濃度域として 0.000 mg/L に丸める上限 */
export const ALCOHOL_DEADBAND_MAX_MGL = 0.05;

/**
 * 検知器の生バイトを mg/L へ変換する。
 * 低濃度域(0.001〜0.050 mg/L)はメーカー仕様に合わせて 0 を返す。
 */
export function rawToAlcoholMgL(raw: number): number {
  if (!Number.isFinite(raw) || raw <= ALCOHOL_DEADBAND_MAX_RAW) return 0;
  return raw / 1000;
}

/**
 * すでに mg/L になっている測定値へ、同じ低濃度域の丸めを適用する。
 * 生バイトから変換する場合は rawToAlcoholMgL を使うこと。
 */
export function normalizeAlcoholMgL(mgL: number): number {
  if (!Number.isFinite(mgL)) return 0;
  // 0.05 は二進で厳密に表せないため、比較にわずかな余裕を持たせる
  return mgL <= ALCOHOL_DEADBAND_MAX_MGL + 1e-9 ? 0 : mgL;
}
